import {ReitHolding} from "@/@types/reit.models.ts";

/**
 * Represents the financial metrics of a Real Estate Investment Trust (REIT).
 */
export type ReitMetrics = {
    marketValue: number;
    costBasis: number;
    unrealizedGainLoss: number;
    unrealizedGainLossPct: number;
    annualIncome: number;
    dividendYield: number;
}

/**
 * Computes key metrics for a Real Estate Investment Trust (REIT) holding.
 *
 * @param {ReitHolding} holding - An object representing the REIT holding, including information about units held,
 * current price per unit, purchase price per unit, and dividend per unit.
 * @return {Object} An object containing:
 * - `marketValue` (number): The total market value of the holding.
 * - `costBasis` (number): The total cost basis of the holding.
 * - `unrealizedGainLoss` (number): The unrealized gain or loss for the holding.
 * - `unrealizedGainLossPct` (number): The unrealized gain or loss percentage relative to the cost basis.
 * - `annualIncome` (number): The annual dividend income for the holding.
 * - `dividendYield` (number): The dividend yield expressed as a percentage of the current price.
 */
export function computeReitMetrics(holding: ReitHolding): ReitMetrics {
    const marketValue = holding.units_held * holding.current_price_per_unit;
    const costBasis = holding.units_held * holding.purchase_price_per_unit;
    const unrealizedGainLoss = marketValue - costBasis;
    const unrealizedGainLossPct = costBasis > 0 ? (unrealizedGainLoss / costBasis) * 100 : 0;
    const annualIncome = holding.units_held * holding.dividend_per_unit;
    const dividendYield =
        holding.current_price_per_unit > 0
            ? (holding.dividend_per_unit / holding.current_price_per_unit) * 100
            : 0;
    return { marketValue, costBasis, unrealizedGainLoss, unrealizedGainLossPct, annualIncome, dividendYield };
}